const API_URL = import.meta.env.VITE_API_URL || 'https://core.talentstar.io/api/v1';

interface ApiResponse<T = unknown> {
  data: T;
  success: boolean;
  message?: string;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    return localStorage.getItem('bts_access_token');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('bts_refresh_token');
  }

  private setTokens(access: string, refresh: string) {
    localStorage.setItem('bts_access_token', access);
    localStorage.setItem('bts_refresh_token', refresh);
  }

  clearTokens() {
    localStorage.removeItem('bts_access_token');
    localStorage.removeItem('bts_refresh_token');
    localStorage.removeItem('bts_user');
  }

  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) {
        this.clearTokens();
        return false;
      }

      const data = await res.json();
      this.setTokens(data.data.access_token, data.data.refresh_token);
      return true;
    } catch {
      this.clearTokens();
      return false;
    }
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    const { body, skipAuth = false, headers: customHeaders, ...rest } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders as Record<string, string>,
    };

    if (!skipAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const config: RequestInit = {
      ...rest,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };

    let res = await fetch(`${this.baseUrl}${endpoint}`, config);

    // Retry once on 401 with token refresh
    if (res.status === 401 && !skipAuth) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        const newToken = this.getToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
        }
        res = await fetch(`${this.baseUrl}${endpoint}`, { ...config, headers });
      }
    }

    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      const message = json.message || json.error || `Error ${res.status}`;
      throw new ApiError(message, res.status, json);
    }

    return {
      data: json.data ?? json,
      success: true,
      message: json.message,
    };
  }

  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  put<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export const apiClient = new ApiClient(API_URL);
export default apiClient;
